import { TopSection } from "src/components/_pages/reservation/top-section";
import { FormSection } from "src/components/_pages/reservation/form-section";
import { ConfirmSection } from "src/components/_pages/reservation/confirm-section";
import { Footer } from "src/components/footer";
import { Loading } from "src/components/loading";
import { useState } from "react";
import { FormData } from "src/types/form";
import {
  phonePattern,
  emailPattern,
  fullWidthDigitPattern,
  fullWidthCharPattern,
  kanaPattern,
  postalCodePattern,
  prefectures,
} from "src/validate";
import { useNavigate } from "react-router-dom";

const Reservation = () => {
  const navigate = useNavigate();
  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    startDate: "",
    endDate: "",
    adultCount: "",
    childCount: "",
    roomType: "",
    name: "",
    kanaName: "",
    phone: "",
    email: "",
    emailConfirm: "",
    postalCode: "",
    prefecture: "",
    city: "",
    options: [],
    body: "",
  });

  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = () => {
    const newErrors: string[] = [];

    if (!formData.startDate) {
      newErrors.push("開始日を選択してください");
    }
    if (!formData.endDate) {
      newErrors.push("終了日を選択してください");
    }
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (start >= end) {
        newErrors.push("開始日は終了日より前の日付を選択してください");
      }
    }

    if (!formData.adultCount) {
      newErrors.push("大人の人数を入力してください");
    } else if (fullWidthCharPattern.test(formData.adultCount)) {
      newErrors.push("大人の人数に全角文字は使用しないでください");
    } else {
      const adult = Number(formData.adultCount);
      if (isNaN(adult) || adult < 1 || adult > 10) {
        newErrors.push("大人の人数は1〜10の範囲で入力してください");
      }
    }

    if (!formData.childCount) {
      newErrors.push("子供の人数を入力してください");
    } else if (fullWidthCharPattern.test(formData.childCount)) {
      newErrors.push("子供の人数に全角文字は使用しないでください");
    } else {
      const child = Number(formData.childCount);
      if (isNaN(child) || child < 0 || child > 10) {
        newErrors.push("子供の人数は0〜10の範囲で入力してください");
      }
    }

    if (!formData.roomType) {
      newErrors.push("部屋タイプを選択してください");
    }

    if (!formData.name) newErrors.push("名前を入力してください");

    if (!formData.kanaName) {
      newErrors.push("ふりがなを入力してください");
    } else if (!kanaPattern.test(formData.kanaName)) {
      newErrors.push("ふりがなは全角ひらがなで入力してください");
    }

    if (!formData.phone) {
      newErrors.push("電話番号を入力してください");
    } else if (fullWidthDigitPattern.test(formData.phone)) {
      newErrors.push("電話番号に全角数字は使用しないでください");
    } else if (formData.phone.includes("-")) {
      newErrors.push("電話番号にハイフンは含めないでください");
    } else if (!phonePattern.test(formData.phone)) {
      newErrors.push("電話番号は10〜11桁の数字で入力してください");
    }

    if (!formData.email) {
      newErrors.push("メールアドレスを入力してください");
    } else if (formData.email && !emailPattern.test(formData.email)) {
      newErrors.push("メールアドレスの形式が正しくありません");
    }

    if (formData.email !== formData.emailConfirm)
      newErrors.push("メールアドレス(再入力)が一致しません");

    if (!formData.postalCode) {
      newErrors.push("郵便番号を入力してください");
    } else if (fullWidthDigitPattern.test(formData.postalCode)) {
      newErrors.push("郵便番号に全角数字は使用しないでください");
    } else if (formData.postalCode.includes("-")) {
      newErrors.push("郵便番号にハイフンは含めないでください");
    } else if (!postalCodePattern.test(formData.postalCode)) {
      newErrors.push("郵便番号は7桁の数字で入力してください");
    }

    if (!formData.prefecture) {
      newErrors.push("都道府県を入力してください");
    } else if (
      formData.prefecture &&
      !prefectures.includes(formData.prefecture)
    ) {
      newErrors.push("都道府県の形式が正しくありません");
    }

    if (!formData.city) newErrors.push("市区町村を入力してください");

    // エラーがある場合
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    // バリデーションOK
    setErrors([]);
    setIsShowConfirm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReservation = async () => {
    try {
      setIsLoading(true);
      console.log(formData);
      const { sendReservationEmailsHttp } = await import('src/firebase');
      await sendReservationEmailsHttp(formData);
      navigate("/reservation/complete");
    } catch (error) {
      console.error("予約処理エラー:", error);
      setErrors(["予約処理中にエラーが発生しました。もう一度お試しください。"]);
      setIsShowConfirm(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <title>石巻マリンヴィレッジ | RESERVATION</title>
      <article>
        <TopSection />
        {isShowConfirm ? (
          <ConfirmSection
            formData={formData}
            onConfirm={handleReservation}
            setIsShowConfirm={setIsShowConfirm}
          />
        ) : (
          <FormSection
            errors={errors}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        )}

        <Footer />
        <Loading isOpen={isLoading} />
      </article>
    </>
  );
};

export default Reservation;
